import './game-event-toast.scss';

import { Flame, Info,Trophy } from 'lucide-react';
import { useEffect,useState } from 'react';

import { formatCurrencyValue, isCurrencyCode } from '@/shared/lib/currency';

export interface GameEventToastProps {
  game: string;
  type: 'win' | 'loss' | 'info';
  mainLine: string;
  subLine?: string;
  countUpAmount?: number;
  countUpCurrency?: string;
}

function useCountUp(target: number, duration = 1100) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    const startTime = performance.now();
    let rafId: number;
    function tick(now: number) {
      const t = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 4); // ease-out quart
      setValue(target * eased);
      if (t < 1) rafId = requestAnimationFrame(tick);
      else setValue(target);
    }
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [target, duration]);
  return value;
}

const ICON = {
  win: <Trophy size={16} />,
  loss: <Flame size={16} />,
  info: <Info size={16} />,
};

function WinToast({
  game,
  countUpAmount,
  countUpCurrency = 'TON',
  subLine,
}: {
  game: string;
  countUpAmount: number;
  countUpCurrency?: string;
  subLine?: string;
}) {
  const animated = useCountUp(countUpAmount);
  const currency = isCurrencyCode(countUpCurrency) ? countUpCurrency : 'TON';
  return (
    <div className="get get--win">
      <div className="get__icon get__icon--win">
        <Trophy size={20} />
      </div>
      <div className="get__body">
        <span className="get__game">{game} · You Won!</span>
        <span className="get__amount">+{formatCurrencyValue(animated, currency)}</span>
        {subLine && <span className="get__sub">{subLine}</span>}
      </div>
    </div>
  );
}

export function GameEventToast({
  game,
  type,
  mainLine,
  subLine,
  countUpAmount,
  countUpCurrency,
}: GameEventToastProps) {
  if (type === 'win' && countUpAmount !== undefined) {
    return (
      <WinToast
        game={game}
        countUpAmount={countUpAmount}
        countUpCurrency={countUpCurrency}
        subLine={subLine}
      />
    );
  }

  return (
    <div className={`get get--${type}`}>
      <div className="get__icon">{ICON[type]}</div>
      <div className="get__body">
        <span className="get__game">{game}</span>
        <span className={`get__main get__main--${type}`}>{mainLine}</span>
        {subLine && <span className="get__sub">{subLine}</span>}
      </div>
    </div>
  );
}
